import React from "react";
import { cn } from "@/lib/utils";
import { agentPermissionHooks } from "@/manager";
import { PermissionKey } from "@/manager/manager/agent-dashboard-permission-manager";

interface PermissionVerifyProps {
  /** 当前功能对应权限字段 */
  permissionKey: PermissionKey;
  /** 白名单列表：在列表中的 key 不走校验逻辑直接放行 */
  whiteList?: (PermissionKey | string)[];
  /** 校验类型：菜单(menu) | 按钮(button)，默认button */
  type?: "menu" | "button";
  /** 无权限处理方式：隐藏(hidden) 或 禁用(disabled)，默认disabled */
  mode?: "hidden" | "disabled";
  /** 无权限回调函数 */
  noAuthCallback?: () => void;
  /** 点击事件（会自动拦截无权限点击） */
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  children: React.ReactNode;
}

/**
 * PermissionVerify - 通用权限校验组件
 * @description 适用于Agent运营后台
 */
export const PermissionVerify: React.FC<PermissionVerifyProps> = ({
  permissionKey,
  whiteList = [],
  type = "button",
  mode = "disabled",
  className,
  children,
  noAuthCallback,
  onClick,
}) => {
  const { canAccessMenu, canClick } = agentPermissionHooks.useUserPermission();

  // 白名单检查：如果在白名单内，直接返回原始内容，不进行权限判断
  const isWhiteListed = whiteList.includes(permissionKey);
  if (isWhiteListed) {
    return (
      <div className={className} onClick={onClick}>
        {children}
      </div>
    );
  }

  const hasMenuAccess = canAccessMenu(permissionKey);
  const hasClickAccess = canClick(permissionKey);

  const handleNoAuthClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 优先调用回调
    if (typeof noAuthCallback === "function") {
      try {
        noAuthCallback();
      } catch (err) {
        console.error("noAuthCallback error", err);
      }
    } else {
      console.warn(`No permission to access: ${permissionKey}`);
    }
  };

  // 菜单级别：无权限直接隐藏或禁用
  if (type === "menu" && !hasMenuAccess) {
    if (mode === "hidden") return null;
    return (
      <div
        role="button"
        aria-disabled="true"
        onClick={handleNoAuthClick}
        className={cn("opacity-50 cursor-not-allowed select-none", className)}
      >
        <div className="pointer-events-none">{children}</div>
      </div>
    );
  }

  // 按钮级别：不可点击或隐藏
  if (type === "button" && !hasClickAccess) {
    if (mode === "hidden") return null;
    return (
      <div
        role="button"
        aria-disabled="true"
        onClick={handleNoAuthClick}
        className={cn("opacity-50 cursor-not-allowed select-none inline-block", className)}
      >
        <div className="pointer-events-none">{children}</div>
      </div>
    );
  }

  return (
    <div className={className} onClick={onClick}>
      {children}
    </div>
  );
};
